import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef, OnDestroy,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {IrbEditor} from '@irb/irb-editors/irb-editors.base';
import {IEvent, IrbAppEvents, IrbEditorsEvents, IrbModelsEvents} from '@irb-common/events';
import {EditorMmcModelService} from '@irb/irb-editors/editor-mmc-model/editor-mmc-model.service';
import {Subscription} from 'rxjs';
import {IAlertDialogData, IUserDetails} from '@irb/app.model';
import {HttpErrorResponse} from '@angular/common/http';
import {IrbEditorsService} from "@irb/irb-editors/irb-editors.service";

@Component({ standalone: false,
    selector: 'irb-editor-mmc-model',
    templateUrl: './editor-mmc-model.component.html',
    styleUrls: ['./editor-mmc-model.component.scss', '../../irb-common/styles/dialog.scss', '../editor-sidebar/editor-sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.None,
    providers:[IrbEditorsService]
})
export class EditorMmcModelComponent extends IrbEditor implements OnDestroy {
    @ViewChild("body") body: ElementRef;
    createModifyData: any = {};
    identifier: string;
    eventModelType: string;
    modelTitle: string;
    defaultExpandedPanels: Array<string>;
    validSections: any;
    private datSub: Subscription;
    modelHistoryData: any;
    donotshow: boolean = false;
    marketingDocuments: any[] = [];
    userDetails: any = {};
    environments: string = null;
    isCustomModelEnabled = false;
    isPublishButtonDisabled = true;

    constructor(private cdr: ChangeDetectorRef,
                private editorMmcModelService: EditorMmcModelService,
                public  localEditorService: IrbEditorsService) {
        super();
        this.identifier = 'modelMarketId';
        this.getRoles();
    }

    get(event: IEvent) {
        this.modelHistoryData = null;
        this.marketingDocuments = [];
        super.get(event);
        this.cdr.detectChanges();
        this.eventModelType = event.data.type;

        this.isCustomModelEnabled = event.data.data && event.data.data.usedFor === 'custom' ? true : false;
        this.modelTitle = this.eventModelType === 'editMmcModel' ? 'Edit Model' : 'Add Model';

        this.defaultExpandedPanels = this.getAllDefaultPanelList();
        this.localEditorService.loadExpansionState(this.defaultExpandedPanels.length, "editMMCModel", this.defaultExpandedPanels);

        if (this.eventModelType === 'editMmcModel') {
            this.datSub = this.editorMmcModelService.getModelDetail(event.data.data.modelMarketId).subscribe((detailData: any) => {
                if (detailData.data.usedFor === 'custom') {
                    if (detailData.data.isSociallyResponsible === null)
                        detailData.data.isSociallyResponsible = -1;
                    if (detailData.data.gipsCompliant == null)
                        detailData.data.gipsCompliant = 'none';
                }

                this.load(detailData.data);
                this.editorMmcModelService.getModelMarketHistory(event.data.data.modelMarketId).subscribe((historyData: any) => {
                    this.modelHistoryData = historyData;
                });
                if (!this.data)
                    return
                this.updateMarketingDocumentsData();
            });
            this.validSections = {
                overview: true,
                securities: true,
                performance: true,
                backcost: true,
                environments: true
            };

        } else {
            this.validSections = {
                overview: false,
                securities: false,
                performance: false,
                backcost: false,
                environments: true
            };
            this.modelHistoryData = null;
            if (event.data && event.data.data && event.data.data.modelMarketName) {
                this.validSections = {
                    overview: true,
                    securities: false,
                    performance: true,
                    backcost: true,
                    environments: true
                };
                event.data.data.status = "Unpublished";
                this.load(event.data.data);
            } else {
                this.load(this.getBlankDataModel());
            }
        }

    }

    load(detailData: any) {
        if (detailData && detailData.securityList && detailData.securityList.length !== 0) {
            detailData.securityList.forEach((sec: any) => {
                sec.isNew = false;
                sec.isUIdeleted = false;
            })
        }
        if(detailData) {
            this.environments = detailData.environments ? detailData.environments.join(" ") : "";
            this.createModifyData = {
                createdDate: detailData.createdDate,
                createdBy: detailData.createdBy,
                modifiedDate: detailData.modifiedDate,
                modifiedBy: detailData.modifiedBy,
                modelName: detailData.modelMarketName
            };

            detailData.isProcessing = detailData.updateStatus === "PROCESSING";
            detailData.isFailed = detailData.updateStatus === "FAILED";
        }

        super.load(detailData);
        if (this.userDetails.isMMCAdmin && this.data.status === 'Unpublished')
            this.checkPublishDisability();
        if (!detailData)
            return;
        this.cdr.detectChanges();
        this.editorMmcModelService.clearDropDownItems();
        this.editorMmcModelService.getMMCDropDowns();
        setTimeout(() => this.onResize());
    }

    onValidChange(e: any) {
        if (!this.validSections) {
            return;
        }
        this.validSections[e.type] = e.valid;
        this.isValid = this.validSections.overview && this.validSections.securities && this.validSections.performance && this.validSections.backcost && this.validSections.environments ? true : false;
    }

    validateEnvironment(_e: any) {
        const env = this.environments && this.environments.replace(/(?:\r\n|\r|\n)/g, ' ').trim();

        this.data.environments = env ? env.split(" ") : [];
        this.onInputChange();
        this.checkPublishDisability();
        if (this.isCustomModelEnabled && this.userDetails.isMMCAdmin && this.data.status === 'Published') {
            if (this.data.environments.length === 0) {
                if (!this.data.advisorFirmId)
                    this.onValidChange({type: 'environments', valid: false})
                else
                    this.onValidChange({type: 'environments', valid: true})
            } else
                this.onValidChange({type: 'environments', valid: true})
        }
    }

    processEvent(event: IEvent) {
        if(event.type === IrbEditorsEvents.MMC_MARKETING_DOCUMENT_UPLOADED) {
            this.updateMarketingDocumentsData();
        } else {
            super.processEvent(event);
        }
        this.cdr.detectChanges();
    }

    getBlankDataModel() {
        const m: any = {
            "modelMarketName": "",
            "assetClass": null,
            "buildingBlockStyle": this.isCustomModelEnabled ? 'none' : null,
            "usedFor": this.isCustomModelEnabled ? 'custom' : null,
            "status": "Unpublished",
            "turnover": null,
            "allocationChanges": this.isCustomModelEnabled ? 'NONE' : null,
            "riskTolerance": null,
            "me": null,
            "t3": null,
            "ytd": null,
            "t12": null,
            "yr3": null,
            "yr5": null,
            "yr10": null,
            "gipsCompliant": this.isCustomModelEnabled ? 'none' : false,
            "performanceAsOf": "",
            "costBPs": this.isCustomModelEnabled ? null : 0,
            "suggestedMinimum": null,
            "morningStarId": null,
            "sourceId": null,
            "assetType": null,
            "securityList": [],
            "exceptionStrategistFeeList": [],
            "isTaxManaged": false,
            "isSociallyResponsible": this.isCustomModelEnabled ? -1 : null,
            "environments": [],
            "isPaidForModel": false,
            "inceptionDate": null,
            "rorInception": null
        };
        return m;
    }

    resubmit() {
        this.as.fireEvent(IrbAppEvents.SHOW_WAIT);

        this.editorMmcModelService.resubmitModel(this.data?.modelMarketId).subscribe(() => {
            this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE);
            this.as.fireEvent(IrbAppEvents.GRID_REFRESH, {"slot": this.slot});
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
        }, () => {
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
        });
    }

    save() {
        let msg: string = '';
        if (this.data && this.data.status === 'Published') {
            msg = "Once the model is saved, the updated information will be visible to all advisors ";
            msg += "who use or view this model. Do you want to continue?";
            this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
                dTitle: 'Confirm',
                dProceedBtnLabel: 'Save',
                dCancelBtnLabel: 'Cancel',
                msg: msg,
                onProceed: () => {
                    this.proceedWithSave()
                }
            } as IAlertDialogData);
        } else {
            msg = "Your changes will be saved, but this model will not be seen by advisors until it is published. ";
            msg += "When you're done creating this model, please contact your Relationship Manager to publish it.";
            this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
                dTitle: "Alert",
                msg: msg,
                dProceedBtnLabel: "Close",
                onProceed: () => {
                    this.proceedWithSave()
                }
            } as IAlertDialogData);
        }
    }

    proceedWithSave() {
        const hasModelId = this.data.modelMarketId ? true : false;
        const dataForSave = this.prepareData(this.data, hasModelId);
        this.as.fireEvent(IrbEditorsEvents.SAVE_START, {"slot": this.slot});

        this.editorMmcModelService.saveUpdateModel(dataForSave).subscribe((response: any) => {
            if (response.success && response.data) {
                const isClosing = this.hasPendingFunction ? true : false;
                this.as.fireEvent(IrbEditorsEvents.SAVE_SUCCESS, {
                    'hasPendingFunction': this.hasPendingFunction,
                    'data': !hasModelId ? 'Model created.' : 'Model updated.',
                    'slot': this.slot
                });
                if (!isClosing) {
                    this.changeAdToEdit(response.data.modelMarketId);
                    this.load(response.data);
                    this.editorMmcModelService.triggerDataSaved(response.data);
                }
                this.as.fireEvent(IrbAppEvents.GRID_REFRESH, {"slot": this.slot});
            } else {
                const msg = response.errorMessages && response.errorMessages.length > 0 ? response.errorMessages[0] : 'An error occurred processing your request.';
                this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot, msg: msg});
            }
        }, () => {
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
        });
    }

    private prepareData(dataForSave: any, hasModelId: boolean) {
        dataForSave = JSON.parse(JSON.stringify(dataForSave));
        delete dataForSave.accountsPerModel;
        delete dataForSave.aumPerModel;
        delete dataForSave.currentHoldings;
        delete dataForSave.envId;
        delete dataForSave.estimatedTransactionFee;
        delete dataForSave.finscore;
        delete dataForSave.isModelUpdated;
        delete dataForSave.isSubscribed;
        delete dataForSave.modelProvider;
        delete dataForSave.modelProviderId;
        delete dataForSave.modelProviderName;
        delete dataForSave.modifiedDate;
        delete dataForSave.performanceModifiedDate;
        delete dataForSave.publishOrAllocationUpdatedDate;
        delete dataForSave.userId;
        delete dataForSave.weightedNetExpenseRatio;
        delete dataForSave.weightedYield;
        if (!hasModelId) {
            delete dataForSave.marketingDocuments;
            delete dataForSave.modelMarketId;
            delete dataForSave.userLoginId;
            delete dataForSave.versionId;
        }

        let o: any;
        if (dataForSave.assetClass) {
            o = {
                id: dataForSave.assetClass.id,
                label: dataForSave.assetClass.label
            };
            dataForSave.assetClass = o;
        }
        if (dataForSave.riskTolerance) {
            o = {
                id: dataForSave.riskTolerance.id,
                label: dataForSave.riskTolerance.label
            };
            dataForSave.riskTolerance = o;
        }
        if (dataForSave.assetType) {
            o = {
                id: dataForSave.assetType.id,
                label: dataForSave.assetType.label
            };
            dataForSave.assetType = o;
        }

        if (dataForSave.performanceAsOf) {
            if (dataForSave.performanceAsOf.toString().indexOf('-') === -1) {
                let d: any = new Date(this.data.performanceAsOf);
                d = d.toLocaleString('en-US', {timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric'});
                dataForSave.performanceAsOf = this.formatDateAsString(new Date(d));
            }
        } else if (dataForSave.usedFor === 'custom') {
            dataForSave.performanceAsOf = null;
        }

        dataForSave.inceptionDate = (dataForSave.inceptionDate) ? this.formatDateAsString(new Date(dataForSave.inceptionDate)) : null;

        if (dataForSave.securityList && dataForSave.securityList.length !== 0) {
            dataForSave.securityList.forEach((sec: any) => {
                delete sec.modelAllocationId;
                delete sec.custCatName;
            })
        }
        if (dataForSave.suggestedMinimum) {
            dataForSave.suggestedMinimum = Number(dataForSave.suggestedMinimum.toString().replace(/,/g, ''));
        }

        if (dataForSave.me === '') {
            dataForSave.me = null;
        }
        if (dataForSave.me !== null) {
            dataForSave.me = dataForSave.me * 1;
        }
        if (dataForSave.t3 === '') {
            dataForSave.t3 = null;
        }
        if (dataForSave.t3 !== null) {
            dataForSave.t3 = dataForSave.t3 * 1;
        }
        if (dataForSave.ytd === '') {
            dataForSave.ytd = null;
        }
        if (dataForSave.ytd !== null) {
            dataForSave.ytd = dataForSave.ytd * 1;
        }
        if (dataForSave.t12 === '') {
            dataForSave.t12 = null;
        }
        if (dataForSave.t12 !== null) {
            dataForSave.t12 = dataForSave.t12 * 1;
        }
        if (dataForSave.yr3 === '') {
            dataForSave.yr3 = null;
        }
        if (dataForSave.yr3 !== null) {
            dataForSave.yr3 = dataForSave.yr3 * 1;
        }
        if (dataForSave.yr5 === '') {
            dataForSave.yr5 = null;
        }
        if (dataForSave.yr5 !== null) {
            dataForSave.yr5 = dataForSave.yr5 * 1;
        }
        if (dataForSave.yr10 === '') {
            dataForSave.yr10 = null;
        }
        if (dataForSave.yr10 !== null) {
            dataForSave.yr10 = dataForSave.yr10 * 1;
        }
        if (dataForSave.rorInception === '') {
            dataForSave.rorInception = null;
        }
        if (dataForSave.rorInception !== null) {
            dataForSave.rorInception = dataForSave.rorInception * 1;
        }

        if (dataForSave.usedFor === 'custom') {
            dataForSave.isSociallyResponsible = dataForSave.isSociallyResponsible === -1 ? null : dataForSave.isSociallyResponsible;
            dataForSave.gipsCompliant = dataForSave.gipsCompliant === 'none' ? null : dataForSave.gipsCompliant;
        }

        return dataForSave;
    }

    ngOnDestroy() {
        if (this.datSub) {
            this.datSub.unsubscribe();
        }
        super.ngOnDestroy();
    }

    private getAllDefaultPanelList() {
        const expandedPanels = ["OVERVIEWMMCMODEL", "SECURITIESMMCMODEL", "PERFORMANCEMMCMODEL", "BACKCOSTMMCMODEL", 'MODELMMCADMINENVIRONMENT', 'MODELHISTORYMMCMODEL'];
        let panels: string[] = [];
            expandedPanels.forEach((componentId: string) => {
                switch(componentId) {
                    case 'OVERVIEWMMCMODEL':
                        panels.push(componentId);
                        break;
                    case 'SECURITIESMMCMODEL':
                        panels.push(componentId);
                        break;
                    case 'PERFORMANCEMMCMODEL':
                        panels.push(componentId);
                        break;
                    case 'BACKCOSTMMCMODEL':
                        panels.push(componentId);
                        break;
                    case 'MODELMMCADMINENVIRONMENT':
                        if (this.eventModelType === "editMmcModel" && this.userDetails && this.userDetails.isMMCAdmin) {
                            panels.push(componentId);
                        }
                        break;
                    case 'MODELHISTORYMMCMODEL':
                        if(this.eventModelType === "editMmcModel") {
                            panels.push(componentId);
                        }
                        break;
                }
            });
        if (this.userDetails.isModelProvider)
            panels.push('OVERVIEWDOCUMENTS');
        return panels;
    }

    private changeAdToEdit(modelMarketId: number) {
        this.eventModelType = 'editMmcModel';
        this.modelTitle = 'Edit Model';
        this.defaultExpandedPanels = this.getAllDefaultPanelList();
        if (!this.donotshow) {
            this.editorMmcModelService.getModelMarketHistory(modelMarketId).subscribe((historyData: any) => {
                this.modelHistoryData = historyData;
            });
        }
    }
    public onViewMarketingDocuments() {
        this.as.fireEvent(IrbEditorsEvents.SHOW_MMC_MARKETING_DOCUMENTS, this.data);
    }

    public updateMarketingDocumentsData() {
        if (this.data && this.data.modelMarketId) {
            this.editorMmcModelService.getMarketingDocuments(this.data?.modelMarketId, false).subscribe((responseData: any) => {
                this.marketingDocuments = responseData;
            }, () => {
            }, () => {
            });
        }
    }

    private getRoles() {
        this.userDetails = this.as.getItem('IRB_APP_CONFIG') as IUserDetails || {};
    }

    unPublish() {
        this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
            dTitle: 'Confirm',
            dProceedBtnLabel: 'Yes',
            dCancelBtnLabel: 'No',
            msg: 'Are you sure you want to unpublish this model?',
            onProceed: () => {
                this.proceedUnpublish()
            }
        } as IAlertDialogData);
    }

    proceedUnpublish() {
        this.as.fireEvent(IrbAppEvents.SHOW_WAIT);
        const params: any = {modelMarketId: this.data?.modelMarketId};
        this.editorMmcModelService.unPublishModel(params).subscribe((response: any) => {
            if (response.success && response.data) {
                this.data.status = 'Unpublished';
                this.data.versionId = response.data.versionId;
                this.load(response.data);
                this.as.fireEvent(IrbAppEvents.GRID_REFRESH, {"slot": this.slot});
            } else {
                this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                    severity: 'error',
                    summary: 'Internal Server Error',
                    detail: 'An error occurred processing your request.'
                });
            }
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
        }, () => {
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
        });
    }

    publish() {
        this.as.fireEvent(IrbAppEvents.SHOW_WAIT);
        const params: any = {modelMarketId: this.data?.modelMarketId};
        this.editorMmcModelService.publishModel(params).subscribe((response: any) => {
            if (response.success && response.data) {
                this.data.status = 'Published';
                this.data.versionId = response.data.versionId;
                this.load(response.data);
                this.as.fireEvent(IrbAppEvents.GRID_REFRESH, {"slot": this.slot});
            } else {
                this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                    severity: 'error',
                    summary: 'Internal Server Error',
                    detail: 'An error occurred processing your request.'
                });
            }
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
        }, () => {
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
        });
    }
    openInModelMarketCenter() {
        let modelData = JSON.parse(JSON.stringify(this.data));
        const eventData: any = {
            type: 'editMMCAdvisorModel',
            data: modelData,
            rowCount: 1,
            rowIndex: 0,
            sortField: null,
            filter: null,
            readOnlyView: true,
            slot:"B"
        };
        this.as.fireEvent(IrbModelsEvents.SHOW_EDIT_MMC_ADVISOR_MODEL, eventData);
    }

    deleteUnPublish() {
        this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
            msg: "Are you sure you want to delete model " + this.data.modelMarketName + "?",
            dProceedBtnLabel: "Yes",
            dCancelBtnLabel: "No",
            onProceed: () => {
                this.proceedWithDelete()
            }
        } as IAlertDialogData);
    }

    proceedWithDelete() {
        this.editorMmcModelService.deleteUnPublish(this.data?.modelMarketId).subscribe((response: any) => {
            if (response.success) {
                this.as.fireEvent(IrbAppEvents.GRID_REFRESH, {"slot": this.slot});
                this.cancel();
            } else {
                this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                    severity: 'error',
                    summary: 'Internal Server Error',
                    detail: 'An error occurred processing your request.'
                });
            }
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
        }, () => {
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
        });
    }

    private formatDateAsString(d: any):String {
        let mon: any = d.getMonth() + 1;

        if (mon < 10) {
            mon = '0' + mon;
        }

        let dd: any = d.getDate();

        if (dd < 10) {
            dd = '0' + dd;
        }

        return d.getFullYear() + '-' + mon + '-' + dd;
    }

    checkPublishDisability() {
        setTimeout(() => {
            this.isPublishButtonDisabled = this.isModified || !this.isValid;
            if (this.isCustomModelEnabled && !this.isModified) {
                if (this.data && !this.data.advisorFirmId && !this.environments)
                    this.isPublishButtonDisabled = true;
                if ((this.data && this.data.advisorFirmId) || this.environments)
                    this.isPublishButtonDisabled = false;
            }
        })
    }
}
